(ns frontend.view.entity-merge.events
  (:require
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.merge :as merge-actions]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [frontend.utils :as fu]
   [potpuri.core :as potpuri :refer [fn->]]
   [reagent.core :as r]
   [reagent.ratom :as ratom]))

(def get-entity session/get-entity)

(defn association-between?
  [entity-id other-id
   {:entity-association/keys [target-entity source-entity]}]
  (or (and (= target-entity entity-id)
           (= source-entity other-id))
      (and (= target-entity other-id)
           (= source-entity entity-id))))

(defn no-associations?
  [{:keys [r-target-id r-source-id]} [_step-kw entity-type]]
  (let [target-id @r-target-id
        source-id @r-source-id
        source-associations (->> @(r/track fu/related-entity-associations entity-type source-id)
                                 (remove (partial association-between? source-id target-id)))
        target-associations (->> @(r/track fu/related-entity-associations entity-type target-id)
                                 (remove (partial association-between? source-id target-id)))]
    (and (empty? target-associations)
         (empty? source-associations))))

(defn empty-step?
  [{:keys [r-no-attachments?
           r-source-guest-accounts
           r-target-guest-accounts
           r-source-kpi-categories
           r-no-kyc?]
    :as   signals} [step-kw _step-args :as step]]
  (case step-kw
    :entity-info
    false

    :associations
    (no-associations? signals step)

    :attachments
    @r-no-attachments?

    :guests
    (and (empty? @r-source-guest-accounts)
         (empty? @r-target-guest-accounts))

    :kpi
    (empty? @r-source-kpi-categories)

    :kyc-profiles
    @r-no-kyc?

    :confirm
    false

    false))

(defn entity-fields
  [{:keys [entity-type r-pipeline-id]}]
  (let [pipeline-id @r-pipeline-id
        hidden-fields #{:common/updated-on :common/updated-by :common/created-on :common/created-by
                        :common/tenant :common/related-entities :common/attachments :db/id
                        :person/intelligence-person :person/fullcontact-person
                        :person/clearbit-person}]
    (->> (registry/get-schema @session/tenant-schema entity-type)
         (remove (fn-> first hidden-fields))
         (filter (fn [[k _]]
                   (state-permissions/field-read-access? k entity-type pipeline-id)))
         (remove (fn [[_k {:keys [field/hidden-from-configuration?]}]]
                   ;;TODO some exceptions might be needed later (e.g. :oppo/target-asset):
                   hidden-from-configuration?))
         (sort-by (fn-> second :field/default-ord))
         keys)))

(defn steps-all
  [{:keys [entity-type review?] :as _signals}]
  (case entity-type
    :entity.type/person
    [[:entity-info :entity.type/person]
     [:associations :entity.type/oppo]
     [:associations :entity.type/person]
     [:associations :entity.type/company]
     [:associations :entity.type/real-estate]
     [:associations :entity.type/fof-asset]
     [:associations :entity.type/other-asset]
     [:associations :entity.type/fundraising-opportunity]
     [:associations :entity.type/investor]
     [:associations :entity.type/fund]
     [:associations :entity.type/fund-commitment]
     [:associations :entity.type/holding]
     [:associations :entity.type/sales-opportunity]
     [:associations :entity.type/sale-agreement]
     [:attachments]
     [:review]]
    :entity.type/company
    [[:entity-info :entity.type/company]
     [:associations :entity.type/oppo]
     [:associations :entity.type/person]
     [:associations :entity.type/company]
     [:associations :entity.type/real-estate]
     [:associations :entity.type/fof-asset]
     [:associations :entity.type/other-asset]
     [:associations :entity.type/fundraising-opportunity]
     [:associations :entity.type/investor]
     [:associations :entity.type/fund]
     [:associations :entity.type/fund-commitment]
     [:associations :entity.type/holding]
     [:associations :entity.type/sales-opportunity]
     [:associations :entity.type/sale-agreement]
     [:attachments]
     [:guests]
     (when review?
       [:kpi])
     [:review]]
    :entity.type/investor
    [[:entity-info :entity.type/investor]
     [:associations :entity.type/person]
     [:associations :entity.type/oppo]
     [:associations :entity.type/investor]
     [:associations :entity.type/holding]
     [:kyc-profiles :entity.type/investor]
     [:attachments]
     [:guests]
     [:review]]
    [[:not-supported]]))

(defn steps
  [signals]
  (->> (steps-all signals)
       (remove nil?)
       (remove (partial empty-step? signals))))

(defn review-steps
  [signals]
  (->> (steps-all (assoc signals :review? true))
       (remove nil?)
       (remove (partial empty-step? signals))))

(defn get-attachments
  [entity]
  (->> entity
       :common/attachments
       (keep get-entity)
       (remove :common/disabled?)
       doall))

(defn create-signals!
  [{:keys [state entity-type]}]
  (let [r-first-entity-id (ratom/reaction (:first-entity-id @state))
        r-second-entity-id (ratom/reaction (:second-entity-id @state))
        r-first-entity (ratom/reaction (get-entity @r-first-entity-id))
        r-second-entity (ratom/reaction (get-entity @r-second-entity-id))
        r-target-id (ratom/reaction (:target-id @state))
        r-source-id (ratom/reaction (:source-id @state))
        r-target (ratom/reaction (get-entity @r-target-id))
        r-source (ratom/reaction (get-entity @r-source-id))
        r-pipeline-id (ratom/reaction
                       (let [target @r-target]
                         (fu/pipeline-id target)))
        r-target-attachments (ratom/reaction
                              (get-attachments @r-target))
        r-source-attachments (ratom/reaction
                              (get-attachments @r-source))
        r-no-attachments? (ratom/reaction
                           (and (empty? @r-target-attachments)
                                (empty? @r-source-attachments)))
        r-source-guest-roles (ratom/reaction
                              (->> @session/roles
                                   (cu/filter-by :role/target @r-source-id)
                                   doall))
        r-source-guest-accounts (ratom/reaction
                                 (->> @r-source-guest-roles
                                      (keep :role/subject)
                                      (keep session/get-entity)
                                      doall))
        r-target-guest-roles (ratom/reaction
                              (->> @session/roles
                                   (cu/filter-by :role/target @r-target-id)
                                   doall))
        r-target-guest-accounts (ratom/reaction
                                 (->> @r-target-guest-roles
                                      (keep :role/subject)
                                      (keep session/get-entity)
                                      doall))
        r-source-kpi-members (ratom/reaction
                              (->> @session/kpi-members
                                   (cu/filter-by :kpi-member/asset @r-source-id)
                                   doall))
        r-source-kpi-categories (ratom/reaction
                                 (->> @r-source-kpi-members
                                      (keep :kpi-member/target)
                                      (keep session/get-entity)
                                      (remove :common/disabled?)
                                      doall))
        enrich-kyc-profile (fn [kyc-profile]
                             (let [a :kyc-profile/latest-process-status
                                   val (fu/get-value @session/tenant
                                                     @session/tenant-schema
                                                     kyc-profile
                                                     a)]
                               (if-not val
                                 kyc-profile
                                 (assoc kyc-profile a val))))
        r-source-kyc-profiles (ratom/reaction
                               (->> @session/kyc-profiles
                                    (cu/filter-by :kyc-profile/target @r-source-id)
                                    (mapv enrich-kyc-profile)))
        r-target-kyc-profiles (ratom/reaction
                               (->> @session/kyc-profiles
                                    (cu/filter-by :kyc-profile/target @r-target-id)
                                    (mapv enrich-kyc-profile)))
        r-no-kyc? (ratom/reaction
                   (or (not (session/kyc?))
                       (and (empty? @r-target-kyc-profiles)
                            (empty? @r-source-kyc-profiles))))
        r-kyc-processes-by-id (ratom/reaction
                               (let [source-processes (->> @r-source-kyc-profiles
                                                           (mapcat :kyc-profile/kyc-processes))
                                     target-processes (->> @r-target-kyc-profiles
                                                           (mapcat :kyc-profile/kyc-processes))]
                                 (->> (concat source-processes target-processes)
                                      (keep session/get-entity)
                                      (mapv (fn [{:keys [db/id] :as e}]
                                              [id e]))
                                      (into {}))))
        r-containing-contact-lists
        (ratom/reaction
         (when (and (= entity-type :entity.type/person)
                    (session/contact-lists?))
           (->> @session/contact-lists
                (filterv (fn [contact-list]
                           (let [members (:contact-list/members contact-list)]
                             (or (some #{@r-first-entity-id} members)
                                 (some #{@r-second-entity-id} members))))))))]
    {:state              state
     :entity-type        entity-type
     :r-pipeline-id      r-pipeline-id
     :r-first-entity-id  r-first-entity-id
     :r-second-entity-id r-second-entity-id
     :r-first-entity     r-first-entity
     :r-second-entity    r-second-entity
     :r-target-id        r-target-id
     :r-source-id        r-source-id
     :r-target           r-target
     :r-source           r-source
     :r-no-attachments?  r-no-attachments?
     :r-source-guest-accounts r-source-guest-accounts
     :r-target-guest-accounts r-target-guest-accounts
     :r-source-kpi-categories r-source-kpi-categories
     :r-source-kyc-profiles r-source-kyc-profiles
     :r-target-kyc-profiles r-target-kyc-profiles
     :r-kyc-processes-by-id r-kyc-processes-by-id
     :r-no-kyc? r-no-kyc?
     :r-containing-contact-lists r-containing-contact-lists}))

(defn toggle!
  [state path val]
  (swap! state
    (fn [s]
      (let [current (get-in s path)
            included? (contains? current val)
            modify (if included?
                     disj
                     conj)
            modify (fnil modify #{})]
        (update-in s path modify val)))))

(defn merge!
  [{:keys [r-target-id r-source-id entity-type state] :as _signals} close-modal!]
  (let [rejection (get @state :rejection)
        selection (get @state :selection)
        msg {:target-id   @r-target-id
             :source-id   @r-source-id
             :entity-type entity-type
             :rejection   rejection
             :selection   selection}]
    (swap! state assoc :merging? true)
    (merge-actions/*->merge
     msg
     (fn [response]
       (let [entity-id (:body response)]
         (close-modal!)
         (routing/open-entity entity-id)))
     (fn [{:keys [error message] :as err}]
       (swap! state (fn [s]
                      (-> s
                          (assoc :error? true
                                 :error-message (cond (string? message)
                                                      message

                                                      (keyword? message)
                                                      (loc/tr message)

                                                      (keyword? error)
                                                      (loc/tr error)

                                                      :else nil))
                          (dissoc :merging?))))
       (logging/error "Entity merge failed:" err)))))
